import { BaseComponent2 } from '../../_common/client/base-component2';

interface SheetContentProps {
    elements: NodeListOf<HTMLElement>;
    withHistory: boolean;
}

interface SheetContentConfig {
    element: HTMLElement;
    title: string;
    withHistory: boolean;
}

export class OpenElementInSheetComponent extends BaseComponent2 {
    public rootSelector = '.js_pdp_open-element-in-sheet';

    public async init(): Promise<void> {
        this.registerChildSheetElements();
    }

    public async onReloadComponentEvent(): Promise<void> {
        this.registerSheetOpenersOnPdp();
    }

    private registerSheetOpenersOnPdp() {
        const sheetContentElements = document.querySelectorAll('[data-ft5-sheet-content-selector]') as NodeListOf<HTMLElement>;
        if (!sheetContentElements.length) {
            return;
        }

        this.appendClickListeners({
            elements: sheetContentElements,
            withHistory: false,
        });
    }

    private registerChildSheetElements() {
        window.o_global.events().on('assets.sheet.contentLoaded', event => {
            this.addClickListenersToActiveSheet(event.id);
        });
    }

    private addClickListenersToActiveSheet(sheetId: string) {
        const currentSheetContentElement = document.getElementById(sheetId) as HTMLElement;
        if (!currentSheetContentElement) {
            console.error('OpenElementInSheetComponent', `Sheet Element with Id not found in DOM`);
            return;
        }

        const contentSheetElements: NodeListOf<HTMLElement> =
            currentSheetContentElement.querySelectorAll('[data-ft5-sheet-content-selector]');

        if (!contentSheetElements.length) {
            return;
        }

        this.appendClickListeners({
            elements: contentSheetElements,
            withHistory: true,
        });
    }

    private appendClickListeners(sheetContent: SheetContentProps) {
        sheetContent.elements.forEach(el => {
            el.addEventListener('click', () => {
                const sheetTitle = el.getAttribute('data-ft5-sheet-title') || '';
                const sheetContentSelector = el.getAttribute('data-ft5-sheet-content-selector');
                const sheetViewTracking = el.getAttribute('data-ft5-sheet-view-tracking');

                if (!sheetContentSelector) {
                    console.error('OpenElementInSheetComponent', `No selector provided`);
                    return;
                }

                if (this.isSheetAlreadyInStack(sheetContentSelector)) {
                    return;
                }

                const sheetContentElement = document.querySelector(sheetContentSelector) as HTMLElement;
                if (!sheetContentElement) {
                    console.error('OpenElementInSheetComponent', `Element not found for ${sheetContentSelector}`);
                    return;
                }

                this.openSheet({
                    element: sheetContentElement.cloneNode(true) as HTMLElement,
                    title: sheetTitle,
                    withHistory: sheetContent.withHistory,
                });

                if (!sheetViewTracking) {
                    return;
                }

                this.performEventTracking(sheetViewTracking, sheetContentSelector);
            });
        });
    }

    private isSheetAlreadyInStack(selector: string) {
        const activeSheet = o_global.pali.sheet.getActiveSheet();
        if (!activeSheet) {
            return false;
        }

        return activeSheet.getContainer().querySelectorAll(`.pl_sheet__content ${selector}`).length;
    }

    private openSheet(config: SheetContentConfig) {
        if (config.withHistory) {
            this.replaceSheetContent(config);
            return;
        }

        this.openFirstSheet(config);
    }

    private openFirstSheet(config: SheetContentConfig) {
        const sheet = new o_global.pali.sheetBuilder({
            title: config.title,
            lockMobileHeight: false,
        });
        sheet.setContent(config.element);
        sheet.open();
    }

    private replaceSheetContent(config: SheetContentConfig) {
        const activeSheet = o_global.pali.sheet.getActiveSheet();
        if (!activeSheet) {
            console.log('OpenElementInSheetComponent: no active sheet found'); // env for logger is not there
            return;
        }

        activeSheet.switchSheetContent({
            title: config.title,
            content: config.element,
            lockMobileHeight: false,
        });
    }

    private performEventTracking(sheetViewTracking: string, sheetContentSelector: string) {
        try {
            const viewTrackingPayload = JSON.parse(sheetViewTracking);
            this.trackEvent(viewTrackingPayload);
        } catch (e) {
            console.error('OpenElementInSheetComponent', `Invalid JSON for sheet view tracking provided by ${sheetContentSelector}`);
        }
    }
}
